import { GOOGLE_APPS_SCRIPT_URL } from '../constants';
import { ApiPayload, AppData, Idea, Topic, ChatMessage } from '../types';

async function fetchGoogleScript(payload: ApiPayload): Promise<any> {
  // We use URL parameters for GET requests (Apps Script doGet) and body for POST
  // However, to simplify for this demo, we will use POST for everything via fetch
  // to avoid large URL limits, assuming the Apps Script handles doPost for all actions.
  
  // Note: Standard fetch to GAS often has CORS issues if not handled correctly.
  // We utilize the text/plain content type to avoid preflight OPTIONS request if possible,
  // or rely on the GAS script setting correct CORS headers.
  
  const response = await fetch(GOOGLE_APPS_SCRIPT_URL, {
    method: 'POST', 
    headers: {
        'Content-Type': 'text/plain;charset=utf-8', 
    },
    body: JSON.stringify(payload)
  });

  const result = await response.json();
  if (result.status === 'error') {
    throw new Error(result.message);
  }
  return result.data;
}

export const api = {
  getInitialData: async (): Promise<{ topics: Topic[], ideas: Idea[] }> => {
    return fetchGoogleScript({ action: 'get' });
  },

  addTopic: async (name: string): Promise<Topic> => {
    return fetchGoogleScript({ action: 'addTopic', data: { name } });
  },

  addIdea: async (idea: Omit<Idea, 'id' | 'createdAt' | 'isDone'>): Promise<Idea> => {
    return fetchGoogleScript({ action: 'addIdea', data: idea });
  },

  updateIdea: async (idea: Idea): Promise<Idea> => {
    return fetchGoogleScript({ action: 'updateIdea', data: idea });
  },

  toggleIdeaStatus: async (id: string, isDone: boolean): Promise<Idea> => {
    return fetchGoogleScript({ action: 'updateIdea', data: { id, isDone, completedAt: isDone ? new Date().toISOString() : null } });
  },

  getChats: async (page: number): Promise<ChatMessage[]> => {
    return fetchGoogleScript({ action: 'getChats', page });
  },

  addChat: async (message: Omit<ChatMessage, 'id' | 'timestamp'>): Promise<ChatMessage> => {
    return fetchGoogleScript({ action: 'addChat', data: message });
  },
  
  verifyPin: async (pin: string): Promise<boolean> => {
    return fetchGoogleScript({ action: 'verifyPin', data: { pin } });
  }
};